import {
  SHORTCUT_ACTIONS,
  formatShortcut,
  formatVersionShortcut,
  sameShortcut,
  shortcutConflict,
  shortcutFromKeyPress,
  shortcutKeys,
  versionShortcutConflict,
  withKey,
  type ShortcutAction,
} from '@lyrmo/shared/shortcuts';
import { useState, type KeyboardEvent } from 'react';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/Dialog';
import { useI18n } from '../../i18n/i18n-context';
import { cx } from '../../lib/cx';
import { FORMAT_KEYS } from '../rewrite/format-keys';
import { SAVE_KEYS } from '../rewrite/useRewriteShortcuts';
import { useShortcuts } from './useShortcuts';
import './shortcuts.css';

// Keys that only modify another: recording waits for the key they go with.
const MODIFIER_KEYS = new Set(['Control', 'Alt', 'AltGraph', 'Shift', 'Meta', 'OS', 'Fn']);

interface ShortcutsDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
}

/** The editor's keyboard shortcuts, each changed by pressing the keys wanted instead. */
export function ShortcutsDialog({ open, onClose }: ShortcutsDialogProps) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onClose={onClose} title={t.shortcuts.title}>
      <ShortcutList onDone={onClose} />
    </Dialog>
  );
}

function ShortcutList({ onDone }: { readonly onDone: () => void }) {
  const { t } = useI18n();
  const { platform, shortcuts, changed, change, reset } = useShortcuts();
  const [recording, setRecording] = useState<ShortcutAction | null>(null);
  const [problem, setProblem] = useState<{ action: ShortcutAction; message: string } | null>(null);
  const names = t.shortcuts.actions;
  const modifiers = platform === 'mac' ? '⌃, ⌥, ⌘' : 'Ctrl, Alt';
  const show = (action: ShortcutAction, keys = shortcuts[action]) =>
    action === 'useVersion'
      ? formatVersionShortcut(keys, platform)
      : formatShortcut(keys, platform);

  function record(action: ShortcutAction, event: KeyboardEvent<HTMLButtonElement>) {
    // The keys are the shortcut being made: nothing else, the dialog included, acts on them.
    event.preventDefault();
    event.stopPropagation();
    if (event.key === 'Escape') {
      setRecording(null);
      setProblem(null);
      return;
    }
    if (MODIFIER_KEYS.has(event.key)) return;
    let keys = shortcutFromKeyPress(event, platform);
    if (!keys) return;
    if (action === 'useVersion') {
      if (!/^(?:Digit|Numpad)\d$/.test(event.code)) {
        setProblem({ action, message: t.shortcuts.needsNumber });
        return;
      }
      // Version 1's keys stand for all of them.
      keys = withKey(keys, '1', platform);
    }
    const shown = show(action, keys);
    const conflict =
      action === 'useVersion'
        ? versionShortcutConflict(keys, platform, 'page')
        : shortcutConflict(keys, platform, 'page');
    if (conflict) {
      setProblem({ action, message: t.shortcuts.conflict[conflict](shown, modifiers) });
      return;
    }
    const wanted = shortcutKeys({ ...shortcuts, [action]: keys }, platform).filter(
      (used) => used.action === action,
    );
    const other = shortcutKeys(shortcuts, platform).find(
      (used) =>
        used.action !== action &&
        wanted.some(({ keys }) => sameShortcut(keys, used.keys, platform)),
    );
    if (other) {
      setProblem({ action, message: t.shortcuts.conflict.taken(shown, names[other.action]) });
      return;
    }
    change(action, keys);
    setRecording(null);
    setProblem(null);
  }

  return (
    <div className="shortcuts">
      <p className="dialog-intro">{t.shortcuts.intro}</p>
      <ul className="shortcut-list">
        {SHORTCUT_ACTIONS.map((action) => {
          const isRecording = recording === action;
          return (
            <li key={action} className="shortcut-row">
              <span className="shortcut-name">{names[action]}</span>
              <button
                type="button"
                className={cx('shortcut-keys', isRecording && 'is-recording')}
                aria-label={
                  isRecording
                    ? t.shortcuts.press(names[action])
                    : t.shortcuts.change(names[action], show(action))
                }
                onClick={() => {
                  setRecording(action);
                  setProblem(null);
                }}
                onBlur={() => {
                  if (isRecording) setRecording(null);
                }}
                onKeyDown={(event) => {
                  if (isRecording) record(action, event);
                }}
              >
                {isRecording ? t.shortcuts.recording : <kbd className="kbd">{show(action)}</kbd>}
              </button>
              {changed.has(action) && !isRecording && (
                <button
                  type="button"
                  className="shortcut-reset"
                  aria-label={t.shortcuts.resetOne(names[action])}
                  onClick={() => {
                    reset(action);
                    setProblem(null);
                  }}
                >
                  {t.shortcuts.reset}
                </button>
              )}
              {problem?.action === action && (
                <p className="shortcut-problem" role="alert">
                  {problem.message}
                </p>
              )}
            </li>
          );
        })}
      </ul>

      <h3 className="shortcuts-subheading">{t.shortcuts.always}</h3>
      <dl className="shortcut-fixed">
        {(
          [
            [t.shortcuts.fixed.close, 'Esc'],
            [t.editor.leaveFocus, 'Esc'],
            [t.shortcuts.fixed.undo, formatShortcut('Mod+Z', platform)],
            [t.shortcuts.fixed.redo, formatShortcut('Mod+Shift+Z', platform)],
            [t.shortcuts.fixed.complete, 'Tab'],
            [t.shortcuts.fixed.save, formatShortcut(SAVE_KEYS, platform)],
            [t.format.bold, formatShortcut(FORMAT_KEYS.strong, platform)],
            [t.format.italic, formatShortcut(FORMAT_KEYS.em, platform)],
            [t.format.underline, formatShortcut(FORMAT_KEYS.underline, platform)],
            [t.format.strike, formatShortcut(FORMAT_KEYS.strike, platform)],
            [t.format.code, formatShortcut(FORMAT_KEYS.code, platform)],
            [t.format.link, formatShortcut(FORMAT_KEYS.link, platform)],
            [t.format.bullets, formatShortcut(FORMAT_KEYS.bullet, platform)],
            [t.format.numbered, formatShortcut(FORMAT_KEYS.ordered, platform)],
            [t.format.checklist, formatShortcut(FORMAT_KEYS.task, platform)],
            [t.format.clear, formatShortcut(FORMAT_KEYS.clear, platform)],
          ] as const
        ).map(([name, keys]) => (
          <div key={name} className="shortcut-row">
            <dt className="shortcut-name">{name}</dt>
            <dd>
              <kbd className="kbd">{keys}</kbd>
            </dd>
          </div>
        ))}
      </dl>

      <div className="shortcuts-footer">
        <Button
          disabled={changed.size === 0}
          onClick={() => {
            reset();
            setProblem(null);
          }}
        >
          {t.shortcuts.resetAll}
        </Button>
        <Button variant="primary" onClick={onDone}>
          {t.common.done}
        </Button>
      </div>
    </div>
  );
}
